import {ALLOCATION_METHOD_ENUM, round2, roundQuantity} from "@/purchase/utils";

// Ventilation d'un achat sur des dossiers : calculs partagés par l'éditeur de ventilation et les écrans de dossier.
// Une part est toujours ramenée à un montant HT (htAmount) ; la clé choisie (montant, pourcentage, quantité) n'est que la façon de la saisir.
// Le serveur reste maître des calculs : ces fonctions ne servent qu'à l'affichage et au préremplissage.

const {AMOUNT, PERCENT, QUANTITY} = ALLOCATION_METHOD_ENUM;

// Part telle que l'API l'attend, d'après la clé choisie et la valeur saisie (le serveur recalcule le montant HT des pourcentages et des quantités)
export function buildAllocationPayload({orderId, lineId, method, value}) {
    const payload = {orderId: String(orderId), method};
    if (lineId) payload.lineId = lineId;
    if (method === PERCENT.value) payload.percent = value;
    else if (method === QUANTITY.value) payload.quantity = value;
    else payload.htAmount = value;
    return payload;
}

// Valeur saisie d'une part enregistrée : sa clé détermine le champ affiché
export function allocationValue(allocation) {
    if (allocation.method === PERCENT.value) return allocation.percent;
    if (allocation.method === QUANTITY.value) return allocation.quantity;
    return allocation.htAmount;
}

// Part enregistrée, renvoyée telle quelle à l'API (pour modifier les autres parts d'un achat sans toucher à celle-ci)
export function toAllocationPayload(allocation) {
    return buildAllocationPayload({
        orderId: allocation.orderId, lineId: allocation.lineId, method: allocation.method ?? AMOUNT.value, value: allocationValue(allocation),
    });
}

// Montant HT d'une saisie. line : ligne d'article visée (si l'achat en a), totalHt : HT de l'achat
export function allocationHt({method, value, line, totalHt}) {
    const amount = Number(value) || 0;
    if (method === PERCENT.value) return round2((line ? line.htAmount : totalHt) * amount / 100);
    if (method === QUANTITY.value) return line && line.quantity > 0 ? round2(amount * line.htAmount / line.quantity) : 0;
    return round2(amount);
}

// Quantité de la ligne que représente une saisie
export function allocationQuantity({method, value, line}) {
    if (!line || !(line.quantity > 0)) return 0;
    const amount = Number(value) || 0;
    if (method === QUANTITY.value) return amount;
    return line.htAmount > 0 ? roundQuantity(line.quantity * allocationHt({method, value, line}) / line.htAmount) : 0;
}

// Ce qui reste à ventiler sur la base d'une saisie (la ligne d'article, ou l'achat entier), en HT ou en quantité.
// rows : saisies {method, value, line} de la même base, sans celle que l'on prépare.
export function remainingOf({rows, line, totalHt}, byQuantity) {
    if (byQuantity && line) {
        return Math.max(0, roundQuantity(line.quantity - rows.reduce((sum, row) => sum + allocationQuantity({...row, line}), 0)));
    }
    const base = line ? line.htAmount : totalHt;
    return Math.max(0, round2(base - rows.reduce((sum, row) => sum + allocationHt({...row, line, totalHt}), 0)));
}

// Parts de l'achat sur un dossier (un dossier peut avoir une part par article)
export function allocationsFor(purchase, orderId) {
    return (purchase.allocations ?? []).filter(allocation => String(allocation.orderId) === String(orderId));
}

// Total HT d'un achat ventilé sur un dossier
export function orderShare(purchase, orderId) {
    return round2(allocationsFor(purchase, orderId).reduce((sum, allocation) => sum + (Number(allocation.htAmount) || 0), 0));
}

// Montant HT d'un achat qui n'est encore ventilé sur aucun dossier
export function unallocatedHt(purchase) {
    const allocated = (purchase.allocations ?? []).reduce((sum, allocation) => sum + (Number(allocation.htAmount) || 0), 0);
    return Math.max(0, round2((Number(purchase.htAmount) || 0) - allocated));
}
